import { virtualDir } from "#skyward_global";
import { DEFAULT_CLOSE_BUTTON, showDialog } from "#skyward_modalLayer";
import { clear } from "./skyward.ui.codeeel";
import { clearValue, disable, enable, getValue, hide, setValue, show } from "./skyward.ui.controls";
import { registerEvents } from "./skyward.ui.events";

const classNames =
{
    fieldset: "fieldset",
    dataObjectViewer: "dataObjectViewer",
    workflowContent: "workflowContent",
    codeEEL: "codeEEL",
    codeEELCode: "codeEELCode"
};

const events =
{
    displayStudentEntityFTEWACalculationPopup,
    handleStatePlacementTestWAIDLoad,
    onIsSubstituteInstructionalStaffLoaded
};

interface widaScreenerDomain
{
    name: string,
    placeValue: number,
    fieldSet?: any,
    div?: any,
    codeInput?: any
};

registerEvents("statereportingwa", events);

function displayStudentEntityFTEWACalculationPopup(this: HTMLElement)
{
    let button = $(this)
    const studentid: bigint = button.data().studentid;
    const entityid: bigint = button.data().entityid;
    const schoolyearid: bigint = button.data().schoolyearid;

    const buildPopup = $.ajax({
        url: `${virtualDir}Enrollment/StudentEntityFTEWA/DisplayStudentEntityFTEWACalculation`,
        type: "POST",
        dataType: "viewparthtml",
        data: { studentid: JSON.stringify(studentid), entityid: JSON.stringify(entityid), schoolyearid: JSON.stringify(schoolyearid) }
    });

    showDialog("Calculated FTE", buildPopup, [DEFAULT_CLOSE_BUTTON], { anchorTo: button });
}

/**
 * Handle the event that the StatePlacementTestWAID code EEL control has
 * been loaded on an EnglishLearnerWA form.
 * @param {any} statePlacementTestWAIDDiv The .codeEEL <div> element jQuery
 * object for the StatePlacementTestWAID control.
 */
function handleStatePlacementTestWAIDLoad(statePlacementTestWAIDDiv: JQuery<HTMLDivElement>)
{
    let formContainer = statePlacementTestWAIDDiv.closest(`.${classNames.dataObjectViewer}`);
    let prefix: string = "";
    if (!formContainer.length)
    {
        formContainer = statePlacementTestWAIDDiv.closest(`.${classNames.workflowContent}`);
        prefix = "EnglishLearnerWA.";
    }

    if (!formContainer)
    {
        return;
    }

    const placementTestScoreInput = formContainer.find(`[name='${prefix}PlacementTestScore']`);
    const stateOverallPlacementTestScoreWAIDFieldSet = formContainer.find(`[name='${prefix}StateOverallPlacementTestScoreWAID']`).closest("fieldset");
    const stateOverallPlacementTestScoreWAIDDiv = stateOverallPlacementTestScoreWAIDFieldSet.find(`.${classNames.codeEEL}`);

    const widaScreenerDomains: widaScreenerDomain[] =
    [
        {
            name: "Listening",
            placeValue: 1000,
        },
        {
            name: "Reading",
            placeValue: 100
        },
        {
            name: "Speaking",
            placeValue: 10
        },
        {
            name: "Writing",
            placeValue: 1
        }
    ];

    for (const widaScreenerDomain of widaScreenerDomains)
    {
        widaScreenerDomain.fieldSet = formContainer.find(`[name='${prefix}StateWIDAScoreWAID${widaScreenerDomain.name}']`).closest("fieldset");
        widaScreenerDomain.div = widaScreenerDomain.fieldSet.find(`.${classNames.codeEEL}`);
        widaScreenerDomain.codeInput = widaScreenerDomain.div.find(`.${classNames.codeEELCode}`);

        widaScreenerDomain.div.on("eelComplete", handleStateWIDAScoreWAIDEELComplete);
    }

    statePlacementTestWAIDDiv.on("eelComplete", handleStatePlacementTestWAIDEELComplete);

    /**
     * Handle the event that the StatePlacementTestWAID code EEL control has
     * been set. This will show/hide/clear any controls that display only when
     * StatePlacementTestWAID is '10 - WIDA Screener' or '11 - WIDA Alt Screener'.
     */

    function handleStatePlacementTestWAIDEELComplete()
    {
        const statePlacementTestCodeEELCode: string = getValue(statePlacementTestWAIDDiv.find('.codeEELCode'));

        // When StatePlacementTestWAID is changed to '10 - WIDA Screener' or '11 - WIDA Alt Screener'.
        if (statePlacementTestCodeEELCode == '10' || statePlacementTestCodeEELCode == '11')
        {
            disable(placementTestScoreInput);

            // Pad the displayed value with zeroes to make it four digits long.
            const currentPlacementTestScore: number = Number.parseInt(getValue(placementTestScoreInput));
            if (!Number.isNaN(currentPlacementTestScore))
            {
                setValue(placementTestScoreInput, padNumberToFourDigits(currentPlacementTestScore))
            }

            for (const widaScreenerDomain of widaScreenerDomains)
            {
                show(widaScreenerDomain.fieldSet);
            }

            show(stateOverallPlacementTestScoreWAIDFieldSet);
        }
        // When StatePlacementTestWAID is changed to something other than '10 - WIDA Screener' or '11 - WIDA Alt Screener'.
        else
        {
            enable(placementTestScoreInput);

            // Remove any padded zeroes from the displayed value.
            const currentPlacementTestScore: number = Number.parseInt(getValue(placementTestScoreInput));
            if (!Number.isNaN(currentPlacementTestScore))
            {
                setValue(placementTestScoreInput, currentPlacementTestScore)
            }

            for (const widaScreenerDomain of widaScreenerDomains)
            {
                if (getValue(widaScreenerDomain.div))
                {
                    clear(widaScreenerDomain.div, true);
                }
                hide(widaScreenerDomain.fieldSet);
            }

            clear(stateOverallPlacementTestScoreWAIDDiv, true);
            hide(stateOverallPlacementTestScoreWAIDFieldSet);
        }
    }

    /**
     * Handle the event that one of the four StateWIDAScoreWAID code EEL controls
     * has been set. If all the WIDA score EELs have a value selected, this will
     * update the value of the PlacementTestScore control accordingly.
     */
    function handleStateWIDAScoreWAIDEELComplete()
    {
        let newPlacementTestScoreValue = 0;
        for (const widaScreenerDomain of widaScreenerDomains)
        {
            const numericDomainValue: number = Number.parseInt(getValue(widaScreenerDomain.codeInput), 10);
            if (typeof numericDomainValue !== 'number' || Number.isNaN(numericDomainValue))
            {
                // If any of the WIDA score code <input>s is empty, don't update PlacementTestScore.
                return;
            }
            newPlacementTestScoreValue += numericDomainValue * widaScreenerDomain.placeValue;
        }
        setValue(placementTestScoreInput, padNumberToFourDigits(newPlacementTestScoreValue));
    }

    function padNumberToFourDigits(number: Number)
    {
        return number.toString().padStart(4, '0');
    }
}

function onIsSubstituteInstructionalStaffLoaded()
{
    $(document).on('browseLoaded.TempSubstituteAssignments', function ()
    {
        $('#TempSubstituteAssignments input[id^="IsInstructionalStaff-"]').each(function (i, browseCheckbox: any)
        {
            const jQueryBrowseCheckbox = $(browseCheckbox);
            if (!jQueryBrowseCheckbox.data().hasStateReportingWAChangeEventHandler)
            {
                jQueryBrowseCheckbox.data('hasStateReportingWAChangeEventHandler', 'true');

                jQueryBrowseCheckbox.on('change', function ()
                {
                    const parts = browseCheckbox.id.split('-');

                    if (parts.length > 0) {
                        const stateTeacherIndicatorCodeEel = $('#StateTeacherIndicatorWAID-' + parts[1]);
                        const stateTeacherIndicatorCodeEelValue = $('#StateTeacherIndicatorWAID-' + parts[1] + '_value');
                        if (!browseCheckbox.checked)
                        {
                            clearValue(stateTeacherIndicatorCodeEel);
                            disable(stateTeacherIndicatorCodeEelValue);
                        }
                        else if (browseCheckbox.checked)
                        {
                            enable(stateTeacherIndicatorCodeEelValue);
                        }
                    }
                })
            }
        })
    })
}